import React, { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import { reportesApi } from '../services/api';
import { useSucursal } from '../context/SucursalContext';
import { formatDate } from '../utils/dateHelpers';

const toDateInputValue = (d) => {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const primerDiaDelMes = () => {
  const hoy = new Date();
  return new Date(hoy.getFullYear(), hoy.getMonth(), 1);
};

const Reportes = () => {
  const { getSucursalId, getSucursalNombre } = useSucursal();
  const [activeTab, setActiveTab] = useState('resumen');
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [fechaDesde, setFechaDesde] = useState(() => toDateInputValue(primerDiaDelMes()));
  const [fechaHasta, setFechaHasta] = useState(() => toDateInputValue(new Date()));

  // Data states
  const [resumen, setResumen] = useState(null);
  const [servicios, setServicios] = useState([]);
  const [tecnicos, setTecnicos] = useState([]);
  const [ingresosMensuales, setIngresosMensuales] = useState([]);
  const [pendientesCobro, setPendientesCobro] = useState({ data: [], total_pendiente: 0 });
  const [comparativo, setComparativo] = useState([]);
  const [clientesFrecuentes, setClientesFrecuentes] = useState([]);
  const [listosSinEntregar, setListosSinEntregar] = useState([]);
  const [tiempoRepuestos, setTiempoRepuestos] = useState({ data: [], resumen: null });
  const [fallasComunes, setFallasComunes] = useState({ data: [], por_marca: [] });
  const [reincidencia, setReincidencia] = useState([]);
  const [rentabilidad, setRentabilidad] = useState([]);
  const [metodosPago, setMetodosPago] = useState([]);
  const [cargaRecepcion, setCargaRecepcion] = useState([]);

  useEffect(() => {
    loadReportData();
  }, [activeTab, getSucursalId()]);

  const loadReportData = async (overrides = {}) => {
    setLoading(true);
    const tienda_id = getSucursalId();
    const desde = overrides.fechaDesde !== undefined ? overrides.fechaDesde : fechaDesde;
    const hasta = overrides.fechaHasta !== undefined ? overrides.fechaHasta : fechaHasta;
    const params = { tienda_id };
    if (desde) params.fecha_desde = desde;
    if (hasta) params.fecha_hasta = hasta;

    try {
      switch (activeTab) {
        case 'resumen':
          const resumenData = await reportesApi.getResumen(params);
          setResumen(resumenData);
          break;
        case 'servicios':
          const serviciosData = await reportesApi.getPorServicios(params);
          setServicios(serviciosData.data || []);
          break;
        case 'tecnicos':
          const tecnicosData = await reportesApi.getPorTecnicos(params);
          setTecnicos(tecnicosData.data || []);
          break;
        case 'ingresos':
          const ingresosData = await reportesApi.getIngresosMensuales(params);
          setIngresosMensuales(ingresosData.data || []);
          break;
        case 'pendientes':
          const pendientesData = await reportesApi.getPendientesCobro(params);
          setPendientesCobro(pendientesData);
          break;
        case 'sucursales':
          const comparativoData = await reportesApi.getComparativoSucursales(params);
          setComparativo(comparativoData.data || []);
          break;
        case 'clientes':
          const clientesData = await reportesApi.getClientesFrecuentes(params);
          setClientesFrecuentes(clientesData.data || []);
          break;
        case 'listos_sin_entregar':
          const listosData = await reportesApi.getListosSinEntregar(params);
          setListosSinEntregar(listosData.data || []);
          break;
        case 'tiempo_repuestos':
          const tiempoData = await reportesApi.getTiempoRepuestos(params);
          setTiempoRepuestos({ data: tiempoData.data || [], resumen: tiempoData.resumen || null });
          break;
        case 'fallas_comunes':
          const fallasData = await reportesApi.getFallasComunes(params);
          setFallasComunes({ data: fallasData.data || [], por_marca: fallasData.por_marca || [] });
          break;
        case 'reincidencia':
          const reincidenciaData = await reportesApi.getReincidencia(params);
          setReincidencia(reincidenciaData.data || []);
          break;
        case 'rentabilidad':
          const rentabilidadData = await reportesApi.getRentabilidad(params);
          setRentabilidad(rentabilidadData.data || []);
          break;
        case 'metodos_pago':
          const metodosData = await reportesApi.getMetodosPago(params);
          setMetodosPago(metodosData.data || []);
          break;
        case 'carga_recepcion':
          const cargaData = await reportesApi.getCargaRecepcion(params);
          setCargaRecepcion(cargaData.data || []);
          break;
        default:
          break;
      }
    } catch (error) {
      console.error('Error cargando reporte:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilter = () => {
    loadReportData();
  };

  const aplicarRangoRapido = (tipo) => {
    const hoy = new Date();
    let desde = hoy;
    let hasta = hoy;

    switch (tipo) {
      case 'hoy':
        desde = hoy;
        hasta = hoy;
        break;
      case 'semana': {
        const diaSemana = hoy.getDay(); // 0 = domingo
        const diff = diaSemana === 0 ? 6 : diaSemana - 1; // lunes como inicio de semana
        desde = new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate() - diff);
        hasta = hoy;
        break;
      }
      case 'mes':
        desde = new Date(hoy.getFullYear(), hoy.getMonth(), 1);
        hasta = hoy;
        break;
      case 'mes_anterior':
        desde = new Date(hoy.getFullYear(), hoy.getMonth() - 1, 1);
        hasta = new Date(hoy.getFullYear(), hoy.getMonth(), 0);
        break;
      case 'anio':
        desde = new Date(hoy.getFullYear(), 0, 1);
        hasta = hoy;
        break;
      case 'todo':
        setFechaDesde('');
        setFechaHasta('');
        loadReportData({ fechaDesde: '', fechaHasta: '' });
        return;
      default:
        break;
    }

    const desdeStr = toDateInputValue(desde);
    const hastaStr = toDateInputValue(hasta);
    setFechaDesde(desdeStr);
    setFechaHasta(hastaStr);
    loadReportData({ fechaDesde: desdeStr, fechaHasta: hastaStr });
  };

  const exportarExcel = async () => {
    setExporting(true);
    const tienda_id = getSucursalId();
    const params = { tienda_id };
    if (fechaDesde) params.fecha_desde = fechaDesde;
    if (fechaHasta) params.fecha_hasta = fechaHasta;

    try {
      const [
        resumenR, serviciosR, tecnicosR, ingresosR, pendientesR, clientesR, sucursalesR,
        listosR, tiempoRepR, fallasR, reincidenciaR, rentabilidadR, metodosR, cargaR,
      ] = await Promise.all([
        reportesApi.getResumen(params),
        reportesApi.getPorServicios(params),
        reportesApi.getPorTecnicos(params),
        reportesApi.getIngresosMensuales(params),
        reportesApi.getPendientesCobro(params),
        reportesApi.getClientesFrecuentes(params),
        reportesApi.getComparativoSucursales(params),
        reportesApi.getListosSinEntregar(params),
        reportesApi.getTiempoRepuestos(params),
        reportesApi.getFallasComunes(params),
        reportesApi.getReincidencia(params),
        reportesApi.getRentabilidad(params),
        reportesApi.getMetodosPago(params),
        reportesApi.getCargaRecepcion(params),
      ]);

      const wb = XLSX.utils.book_new();

      const resumenRows = [
        { metrica: 'Total Órdenes', valor: resumenR.totales?.total_ordenes || 0 },
        { metrica: 'Total Ingresos', valor: parseFloat(resumenR.totales?.total_ingresos) || 0 },
        { metrica: 'Pendiente de Cobro', valor: parseFloat(resumenR.totales?.total_pendiente) || 0 },
        { metrica: 'Ticket Promedio', valor: parseFloat(resumenR.totales?.ticket_promedio) || 0 },
        { metrica: 'Órdenes Hoy', valor: resumenR.hoy?.cantidad || 0 },
        { metrica: 'Órdenes Esta Semana', valor: resumenR.semana?.cantidad || 0 },
        { metrica: 'Órdenes Este Mes', valor: resumenR.mes?.cantidad || 0 },
      ];

      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(resumenRows), 'Resumen');
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(resumenR.por_status || []), 'Por Estado');
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(serviciosR.data || []), 'Por Servicios');
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(tecnicosR.data || []), 'Por Tecnicos');
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(ingresosR.data || []), 'Ingresos Mensuales');
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(pendientesR.data || []), 'Pendientes Cobro');
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(clientesR.data || []), 'Clientes Frecuentes');
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(sucursalesR.data || []), 'Comparativo Sucursales');
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(listosR.data || []), 'Equipos Sin Entregar');
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(tiempoRepR.data || []), 'Tiempo de Repuestos');
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(fallasR.data || []), 'Fallas Comunes');
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(fallasR.por_marca || []), 'Fallas por Marca');
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(reincidenciaR.data || []), 'Reincidencia');
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rentabilidadR.data || []), 'Rentabilidad');
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(metodosR.data || []), 'Metodos de Pago');
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(cargaR.data || []), 'Carga de Recepcion');

      const fecha = new Date().toISOString().slice(0, 10);
      XLSX.writeFile(wb, `reportes_intersecom_${fecha}.xlsx`);
    } catch (error) {
      console.error('Error exportando a Excel:', error);
      alert('Error al exportar a Excel');
    } finally {
      setExporting(false);
    }
  };

  const formatMoney = (amount) => `Q${parseFloat(amount || 0).toLocaleString('es-GT', { minimumFractionDigits: 2 })}`;

  const tabs = [
    { id: 'resumen', label: 'Resumen General' },
    { id: 'servicios', label: 'Por Servicios' },
    { id: 'tecnicos', label: 'Por Técnicos' },
    { id: 'ingresos', label: 'Ingresos Mensuales' },
    { id: 'pendientes', label: 'Pendientes de Cobro' },
    { id: 'clientes', label: 'Clientes Frecuentes' },
    { id: 'sucursales', label: 'Comparativo Sucursales' },
    { id: 'listos_sin_entregar', label: 'Sin Entregar' },
    { id: 'tiempo_repuestos', label: 'Tiempo de Repuestos' },
    { id: 'fallas_comunes', label: 'Fallas Comunes' },
    { id: 'reincidencia', label: 'Reincidencia' },
    { id: 'rentabilidad', label: 'Rentabilidad' },
    { id: 'metodos_pago', label: 'Métodos de Pago' },
    { id: 'carga_recepcion', label: 'Carga de Recepción' },
  ];

  const getMaxValue = (data, key) => Math.max(...data.map(d => parseFloat(d[key]) || 0), 1);

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Reportes</h1>
          <p className="page-subtitle">{getSucursalNombre()}</p>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button onClick={exportarExcel} className="btn btn-secondary" disabled={exporting}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
              <polyline points="14 2 14 8 20 8"/>
              <line x1="9" y1="13" x2="15" y2="19"/>
              <line x1="15" y1="13" x2="9" y2="19"/>
            </svg>
            {exporting ? 'Exportando...' : 'Exportar a Excel'}
          </button>
          <button onClick={() => window.print()} className="btn btn-secondary">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
              <polyline points="6 9 6 2 18 2 18 9"/>
              <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/>
              <rect x="6" y="14" width="12" height="8"/>
            </svg>
            Imprimir
          </button>
        </div>
      </div>

      {/* Filtros */}
      <div className="report-filters">
        <div className="form-group">
          <label className="form-label">Desde</label>
          <input
            type="date"
            className="form-control"
            value={fechaDesde}
            onChange={(e) => setFechaDesde(e.target.value)}
          />
        </div>
        <div className="form-group">
          <label className="form-label">Hasta</label>
          <input
            type="date"
            className="form-control"
            value={fechaHasta}
            onChange={(e) => setFechaHasta(e.target.value)}
          />
        </div>
        <div className="form-group" style={{ display: 'flex', alignItems: 'flex-end' }}>
          <button onClick={handleFilter} className="btn btn-primary">
            Aplicar Filtros
          </button>
        </div>
        <div className="form-group" style={{ display: 'flex', alignItems: 'flex-end', gap: '8px', flexWrap: 'wrap' }}>
          <button onClick={() => aplicarRangoRapido('hoy')} className="btn btn-secondary btn-sm">Hoy</button>
          <button onClick={() => aplicarRangoRapido('semana')} className="btn btn-secondary btn-sm">Esta Semana</button>
          <button onClick={() => aplicarRangoRapido('mes')} className="btn btn-secondary btn-sm">Este Mes</button>
          <button onClick={() => aplicarRangoRapido('mes_anterior')} className="btn btn-secondary btn-sm">Mes Anterior</button>
          <button onClick={() => aplicarRangoRapido('anio')} className="btn btn-secondary btn-sm">Este Año</button>
          <button onClick={() => aplicarRangoRapido('todo')} className="btn btn-secondary btn-sm">Todo</button>
        </div>
      </div>

      {/* Tabs */}
      <div className="report-tabs">
        {tabs.map(tab => (
          <button
            key={tab.id}
            className={`report-tab ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="loading">
          <div className="spinner"></div>
        </div>
      ) : (
        <>
          {/* Resumen General */}
          {activeTab === 'resumen' && resumen && (
            <div>
              <div className="stats-grid">
                <div className="stat-card">
                  <div className="stat-icon blue">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                      <polyline points="14 2 14 8 20 8"/>
                    </svg>
                  </div>
                  <div className="stat-info">
                    <h3>{resumen.totales?.total_ordenes || 0}</h3>
                    <p>Total Órdenes</p>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon green">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="12" y1="1" x2="12" y2="23"/>
                      <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
                    </svg>
                  </div>
                  <div className="stat-info">
                    <h3>{formatMoney(resumen.totales?.total_ingresos)}</h3>
                    <p>Total Ingresos</p>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon orange">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10"/>
                      <polyline points="12 6 12 12 16 14"/>
                    </svg>
                  </div>
                  <div className="stat-info">
                    <h3>{formatMoney(resumen.totales?.total_pendiente)}</h3>
                    <p>Pendiente de Cobro</p>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon blue">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="18" y1="20" x2="18" y2="10"/>
                      <line x1="12" y1="20" x2="12" y2="4"/>
                      <line x1="6" y1="20" x2="6" y2="14"/>
                    </svg>
                  </div>
                  <div className="stat-info">
                    <h3>{formatMoney(resumen.totales?.ticket_promedio)}</h3>
                    <p>Ticket Promedio</p>
                  </div>
                </div>
              </div>

              <div className="stats-grid">
                <div className="stat-card">
                  <div className="stat-info">
                    <h3>{resumen.hoy?.cantidad || 0}</h3>
                    <p>Órdenes Hoy ({formatMoney(resumen.hoy?.monto)})</p>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-info">
                    <h3>{resumen.semana?.cantidad || 0}</h3>
                    <p>Esta Semana ({formatMoney(resumen.semana?.monto)})</p>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-info">
                    <h3>{resumen.mes?.cantidad || 0}</h3>
                    <p>Este Mes ({formatMoney(resumen.mes?.monto)})</p>
                  </div>
                </div>
              </div>

              <div className="card">
                <h3 className="card-title" style={{ marginBottom: '20px' }}>Por Estado</h3>
                <div className="bar-chart">
                  {resumen.por_status?.map(item => {
                    const maxMonto = getMaxValue(resumen.por_status, 'monto');
                    const width = (parseFloat(item.monto) / maxMonto) * 100;
                    return (
                      <div key={item.status} className="bar-item">
                        <span className="bar-label" style={{ textTransform: 'capitalize' }}>{item.status}</span>
                        <div className="bar-container">
                          <div
                            className={`bar-fill ${item.status === 'entregada' ? 'green' : item.status === 'terminada' ? 'blue' : 'orange'}`}
                            style={{ width: `${width}%` }}
                          />
                        </div>
                        <span className="bar-value">{item.cantidad} ({formatMoney(item.monto)})</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Por Servicios */}
          {activeTab === 'servicios' && (
            <div className="card">
              <h3 className="card-title" style={{ marginBottom: '20px' }}>Órdenes por Tipo de Servicio</h3>
              {servicios.length === 0 ? (
                <p style={{ color: 'var(--text-light)' }}>No hay datos para mostrar</p>
              ) : (
                <div className="bar-chart">
                  {servicios.map(item => {
                    const maxCantidad = getMaxValue(servicios, 'cantidad');
                    const width = (parseInt(item.cantidad) / maxCantidad) * 100;
                    return (
                      <div key={item.tipo_servicio} className="bar-item">
                        <span className="bar-label">{item.tipo_servicio}</span>
                        <div className="bar-container">
                          <div className="bar-fill blue" style={{ width: `${width}%` }} />
                        </div>
                        <span className="bar-value">{item.cantidad} - {formatMoney(item.ingresos)}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Por Técnicos */}
          {activeTab === 'tecnicos' && (
            <div>
              {tecnicos.length > 0 && (
                <div className="stats-grid" style={{ gridTemplateColumns: '1fr 1fr' }}>
                  <div className="card">
                    <h3 className="card-title" style={{ marginBottom: '20px' }}>Órdenes por Técnico</h3>
                    <div className="bar-chart">
                      {tecnicos.map(tec => {
                        const maxCantidad = getMaxValue(tecnicos, 'cantidad');
                        const width = (parseInt(tec.cantidad) / maxCantidad) * 100;
                        return (
                          <div key={tec.tecnico} className="bar-item">
                            <span className="bar-label">{tec.tecnico || 'Sin asignar'}</span>
                            <div className="bar-container">
                              <div className="bar-fill blue" style={{ width: `${width}%` }} />
                            </div>
                            <span className="bar-value">{tec.cantidad}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  <div className="card">
                    <h3 className="card-title" style={{ marginBottom: '20px' }}>Eficiencia por Técnico</h3>
                    <div className="bar-chart">
                      {tecnicos.map(tec => (
                        <div key={tec.tecnico} className="bar-item">
                          <span className="bar-label">{tec.tecnico || 'Sin asignar'}</span>
                          <div className="bar-container">
                            <div className="bar-fill green" style={{ width: `${tec.eficiencia || 0}%` }} />
                          </div>
                          <span className="bar-value">{tec.eficiencia != null ? `${tec.eficiencia}%` : '-'}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              <div className="card">
              <h3 className="card-title" style={{ marginBottom: '20px' }}>Rendimiento por Técnico</h3>
              {tecnicos.length === 0 ? (
                <p style={{ color: 'var(--text-light)' }}>No hay datos para mostrar</p>
              ) : (
                <div className="table-container">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Técnico</th>
                        <th>Órdenes</th>
                        <th>Completadas</th>
                        <th>Eficiencia</th>
                        <th>Días prom. a diagnóstico</th>
                        <th>Días prom. a entrega</th>
                        <th>Ingresos</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tecnicos.map((tec, idx) => (
                        <tr key={idx}>
                          <td style={{ fontWeight: 500 }}>{tec.tecnico || 'Sin asignar'}</td>
                          <td>{tec.cantidad}</td>
                          <td>
                            <span className="status-badge status-terminada">
                              {tec.completadas}
                            </span>
                          </td>
                          <td>{tec.eficiencia != null ? `${tec.eficiencia}%` : '-'}</td>
                          <td>{tec.dias_promedio_recepcion != null ? `${tec.dias_promedio_recepcion} días` : '-'}</td>
                          <td>{tec.dias_promedio_entrega != null ? `${tec.dias_promedio_entrega} días` : '-'}</td>
                          <td style={{ fontWeight: 500 }}>{formatMoney(tec.ingresos)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              </div>
            </div>
          )}

          {/* Ingresos Mensuales */}
          {activeTab === 'ingresos' && (
            <div className="card">
              <h3 className="card-title" style={{ marginBottom: '20px' }}>Ingresos Mensuales (Últimos 12 meses)</h3>
              {ingresosMensuales.length === 0 ? (
                <p style={{ color: 'var(--text-light)' }}>No hay datos para mostrar</p>
              ) : (
                <div className="bar-chart">
                  {ingresosMensuales.map(item => {
                    const maxIngresos = getMaxValue(ingresosMensuales, 'ingresos');
                    const width = (parseFloat(item.ingresos) / maxIngresos) * 100;
                    return (
                      <div key={item.mes} className="bar-item">
                        <span className="bar-label">{item.mes}</span>
                        <div className="bar-container">
                          <div className="bar-fill green" style={{ width: `${width}%` }} />
                        </div>
                        <span className="bar-value">{item.cantidad} - {formatMoney(item.ingresos)}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Pendientes de Cobro */}
          {activeTab === 'pendientes' && (
            <div>
              <div className="stats-grid">
                <div className="stat-card">
                  <div className="stat-icon red">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10"/>
                      <line x1="12" y1="8" x2="12" y2="12"/>
                      <line x1="12" y1="16" x2="12.01" y2="16"/>
                    </svg>
                  </div>
                  <div className="stat-info">
                    <h3>{formatMoney(pendientesCobro.total_pendiente)}</h3>
                    <p>Total Pendiente ({pendientesCobro.cantidad || 0} órdenes)</p>
                  </div>
                </div>
              </div>

              <div className="card">
                <h3 className="card-title" style={{ marginBottom: '20px' }}>Detalle de Pendientes</h3>
                {pendientesCobro.data?.length === 0 ? (
                  <p style={{ color: 'var(--text-light)' }}>No hay pagos pendientes</p>
                ) : (
                  <div className="table-container">
                    <table className="table">
                      <thead>
                        <tr>
                          <th>N° Orden</th>
                          <th>Cliente</th>
                          <th>Teléfono</th>
                          <th>Fecha</th>
                          <th>Total</th>
                          <th>Anticipo</th>
                          <th>Saldo</th>
                          <th>Estado</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pendientesCobro.data?.map(orden => (
                          <tr key={orden.numero_orden}>
                            <td style={{ fontWeight: 600 }}>#{orden.numero_orden}</td>
                            <td>{orden.nombre_cliente}</td>
                            <td>{orden.telefono_cliente || '-'}</td>
                            <td>{formatDate(orden.fecha_recepcion)}</td>
                            <td>{formatMoney(orden.valor_total)}</td>
                            <td>{formatMoney(orden.anticipo)}</td>
                            <td style={{ fontWeight: 600, color: 'var(--danger-color)' }}>
                              {formatMoney(orden.saldo)}
                            </td>
                            <td>
                              <span className={`status-badge status-${orden.status}`}>
                                {orden.status}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Clientes Frecuentes */}
          {activeTab === 'clientes' && (
            <div className="card">
              <h3 className="card-title" style={{ marginBottom: '20px' }}>Clientes Frecuentes</h3>
              {clientesFrecuentes.length === 0 ? (
                <p style={{ color: 'var(--text-light)' }}>No hay datos para mostrar</p>
              ) : (
                <div className="table-container">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Cliente</th>
                        <th>Teléfono</th>
                        <th>Total Órdenes</th>
                        <th>Total Gastado</th>
                        <th>Última Visita</th>
                      </tr>
                    </thead>
                    <tbody>
                      {clientesFrecuentes.map((cliente, idx) => (
                        <tr key={idx}>
                          <td style={{ fontWeight: 500 }}>{cliente.nombre_cliente}</td>
                          <td>{cliente.telefono_cliente || '-'}</td>
                          <td>
                            <span className="status-badge status-proceso">
                              {cliente.total_ordenes}
                            </span>
                          </td>
                          <td style={{ fontWeight: 500 }}>{formatMoney(cliente.total_gastado)}</td>
                          <td>{formatDate(cliente.ultima_visita)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Comparativo Sucursales */}
          {activeTab === 'sucursales' && (
            <div>
              <div className="stats-grid">
                {comparativo.map(suc => (
                  <div key={suc.tienda_id} className="sucursal-card">
                    <div className="sucursal-card-header">
                      <h4 className="sucursal-card-title">{suc.tienda}</h4>
                    </div>
                    <div className="sucursal-stats">
                      <div className="sucursal-stat">
                        <div className="sucursal-stat-value">{suc.total_ordenes}</div>
                        <div className="sucursal-stat-label">Órdenes</div>
                      </div>
                      <div className="sucursal-stat">
                        <div className="sucursal-stat-value" style={{ fontSize: '18px' }}>
                          {formatMoney(suc.total_ingresos)}
                        </div>
                        <div className="sucursal-stat-label">Ingresos</div>
                      </div>
                      <div className="sucursal-stat">
                        <div className="sucursal-stat-value" style={{ color: 'var(--danger-color)', fontSize: '18px' }}>
                          {formatMoney(suc.total_pendiente)}
                        </div>
                        <div className="sucursal-stat-label">Pendiente</div>
                      </div>
                    </div>
                    <div style={{ marginTop: '16px', display: 'flex', gap: '8px', justifyContent: 'center', flexWrap: 'wrap' }}>
                      <span className="status-badge status-pendiente">{suc.pendientes} Pend.</span>
                      <span className="status-badge status-proceso">{suc.en_proceso} Proc.</span>
                      <span className="status-badge status-terminada">{suc.terminadas} Term.</span>
                      <span className="status-badge status-entregada">{suc.entregadas} Entr.</span>
                    </div>
                  </div>
                ))}
              </div>

              {comparativo.length > 0 && (
                <div className="card">
                  <h3 className="card-title" style={{ marginBottom: '20px' }}>Comparativo de Ingresos</h3>
                  <div className="bar-chart">
                    {comparativo.map(suc => {
                      const maxIngresos = getMaxValue(comparativo, 'total_ingresos');
                      const width = (parseFloat(suc.total_ingresos) / maxIngresos) * 100;
                      return (
                        <div key={suc.tienda_id} className="bar-item">
                          <span className="bar-label">{suc.tienda}</span>
                          <div className="bar-container">
                            <div className="bar-fill green" style={{ width: `${width}%` }} />
                          </div>
                          <span className="bar-value">{formatMoney(suc.total_ingresos)}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Equipos Sin Entregar */}
          {activeTab === 'listos_sin_entregar' && (
            <div className="card">
              <h3 className="card-title" style={{ marginBottom: '20px' }}>Equipos Terminados Sin Entregar</h3>
              <p style={{ color: 'var(--text-light)', marginBottom: '16px', fontSize: '14px' }}>
                Días calculados desde la recepción del equipo (no se guarda la fecha en que pasó a "terminada").
              </p>
              {listosSinEntregar.length === 0 ? (
                <p style={{ color: 'var(--text-light)' }}>No hay equipos terminados pendientes de entrega</p>
              ) : (
                <div className="table-container">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>N° Orden</th>
                        <th>Cliente</th>
                        <th>Teléfono</th>
                        <th>Fecha Recepción</th>
                        <th>Días desde Recepción</th>
                        <th>Total</th>
                        <th>Saldo</th>
                        <th>Sucursal</th>
                      </tr>
                    </thead>
                    <tbody>
                      {listosSinEntregar.map((orden, idx) => (
                        <tr key={idx}>
                          <td style={{ fontWeight: 600 }}>#{orden.numero_orden}</td>
                          <td>{orden.nombre_cliente}</td>
                          <td>{orden.telefono_cliente || '-'}</td>
                          <td>{formatDate(orden.fecha_recepcion)}</td>
                          <td>
                            <span className={`status-badge ${orden.dias_desde_recepcion > 7 ? 'status-pendiente' : 'status-terminada'}`}>
                              {orden.dias_desde_recepcion} días
                            </span>
                          </td>
                          <td>{formatMoney(orden.valor_total)}</td>
                          <td style={{ fontWeight: 600, color: orden.saldo > 0 ? 'var(--danger-color)' : 'inherit' }}>
                            {formatMoney(orden.saldo)}
                          </td>
                          <td>{orden.tienda || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Tiempo de Repuestos */}
          {activeTab === 'tiempo_repuestos' && (
            <div>
              {tiempoRepuestos.resumen && (
                <div className="stats-grid">
                  <div className="stat-card">
                    <div className="stat-info">
                      <h3>{tiempoRepuestos.resumen.dias_promedio_espera ?? '-'}</h3>
                      <p>Días Promedio de Espera</p>
                    </div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-info">
                      <h3>{tiempoRepuestos.resumen.cantidad || 0}</h3>
                      <p>Órdenes con Repuesto Registrado</p>
                    </div>
                  </div>
                </div>
              )}
              <div className="card">
                <h3 className="card-title" style={{ marginBottom: '20px' }}>Días de Espera por Proveedor</h3>
                {tiempoRepuestos.data.length === 0 ? (
                  <p style={{ color: 'var(--text-light)' }}>No hay datos de repuestos para mostrar</p>
                ) : (
                  <div className="table-container">
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Proveedor / Lugar de Cotización</th>
                          <th>Órdenes</th>
                          <th>Días Promedio</th>
                          <th>Días Máximo</th>
                        </tr>
                      </thead>
                      <tbody>
                        {tiempoRepuestos.data.map((row, idx) => (
                          <tr key={idx}>
                            <td style={{ fontWeight: 500 }}>{row.proveedor}</td>
                            <td>{row.cantidad}</td>
                            <td>{row.dias_promedio_espera} días</td>
                            <td>{row.dias_maximo_espera} días</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Fallas Comunes */}
          {activeTab === 'fallas_comunes' && (
            <div>
              {fallasComunes.por_marca.length > 0 && (
                <div className="card">
                  <h3 className="card-title" style={{ marginBottom: '20px' }}>Marcas Más Frecuentes</h3>
                  <div className="bar-chart">
                    {fallasComunes.por_marca.map((item, idx) => {
                      const maxCantidad = getMaxValue(fallasComunes.por_marca, 'cantidad');
                      const width = (parseInt(item.cantidad) / maxCantidad) * 100;
                      return (
                        <div key={idx} className="bar-item">
                          <span className="bar-label">{item.marca}</span>
                          <div className="bar-container">
                            <div className="bar-fill blue" style={{ width: `${width}%` }} />
                          </div>
                          <span className="bar-value">{item.cantidad}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              <div className="card">
                <h3 className="card-title" style={{ marginBottom: '20px' }}>Fallas Más Comunes (por Marca)</h3>
                {fallasComunes.data.length === 0 ? (
                  <p style={{ color: 'var(--text-light)' }}>No hay datos para mostrar</p>
                ) : (
                  <div className="table-container">
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Marca</th>
                          <th>Falla</th>
                          <th>Cantidad</th>
                        </tr>
                      </thead>
                      <tbody>
                        {fallasComunes.data.map((row, idx) => (
                          <tr key={idx}>
                            <td style={{ fontWeight: 500 }}>{row.marca}</td>
                            <td>{row.falla}</td>
                            <td>
                              <span className="status-badge status-proceso">{row.cantidad}</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Reincidencia */}
          {activeTab === 'reincidencia' && (
            <div className="card">
              <h3 className="card-title" style={{ marginBottom: '20px' }}>Clientes con Posible Reincidencia</h3>
              <p style={{ color: 'var(--text-light)', marginBottom: '16px', fontSize: '14px' }}>
                Mismo cliente y misma marca de equipo en más de una orden dentro de 90 días.
              </p>
              {reincidencia.length === 0 ? (
                <p style={{ color: 'var(--text-light)' }}>No se encontraron casos de reincidencia</p>
              ) : (
                <div className="table-container">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Cliente</th>
                        <th>Teléfono</th>
                        <th>Marca</th>
                        <th>Orden Anterior</th>
                        <th>Falla Anterior</th>
                        <th>Orden Nueva</th>
                        <th>Falla Nueva</th>
                        <th>Días Entre Visitas</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reincidencia.map((row, idx) => (
                        <tr key={idx}>
                          <td style={{ fontWeight: 500 }}>{row.nombre_cliente}</td>
                          <td>{row.telefono_cliente}</td>
                          <td>{row.marca}</td>
                          <td>#{row.orden_anterior} ({formatDate(row.fecha_anterior)})</td>
                          <td>{row.falla_anterior}</td>
                          <td>#{row.orden_nueva} ({formatDate(row.fecha_nueva)})</td>
                          <td>{row.falla_nueva}</td>
                          <td>
                            <span className="status-badge status-pendiente">{row.dias_entre_visitas} días</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Rentabilidad */}
          {activeTab === 'rentabilidad' && (
            <div>
              {rentabilidad.length > 0 && (
                <div className="card">
                  <h3 className="card-title" style={{ marginBottom: '20px' }}>Margen por Tipo de Servicio</h3>
                  <div className="bar-chart">
                    {rentabilidad.map((item, idx) => {
                      const maxMargen = getMaxValue(rentabilidad, 'margen');
                      const width = (parseFloat(item.margen) / maxMargen) * 100;
                      return (
                        <div key={idx} className="bar-item">
                          <span className="bar-label">{item.tipo_servicio}</span>
                          <div className="bar-container">
                            <div className="bar-fill green" style={{ width: `${Math.max(width, 0)}%` }} />
                          </div>
                          <span className="bar-value">{formatMoney(item.margen)} ({item.margen_pct}%)</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              <div className="card">
                <h3 className="card-title" style={{ marginBottom: '20px' }}>Detalle de Rentabilidad</h3>
                {rentabilidad.length === 0 ? (
                  <p style={{ color: 'var(--text-light)' }}>No hay datos para mostrar</p>
                ) : (
                  <div className="table-container">
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Tipo de Servicio</th>
                          <th>Órdenes</th>
                          <th>Ingresos</th>
                          <th>Costos</th>
                          <th>Margen</th>
                          <th>% Margen</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rentabilidad.map((row, idx) => (
                          <tr key={idx}>
                            <td style={{ fontWeight: 500 }}>{row.tipo_servicio}</td>
                            <td>{row.cantidad}</td>
                            <td>{formatMoney(row.ingresos)}</td>
                            <td>{formatMoney(row.costos)}</td>
                            <td style={{ fontWeight: 600 }}>{formatMoney(row.margen)}</td>
                            <td>{row.margen_pct}%</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Métodos de Pago */}
          {activeTab === 'metodos_pago' && (
            <div className="card">
              <h3 className="card-title" style={{ marginBottom: '20px' }}>Distribución por Método de Pago</h3>
              {metodosPago.length === 0 ? (
                <p style={{ color: 'var(--text-light)' }}>No hay datos para mostrar</p>
              ) : (
                <div className="bar-chart">
                  {metodosPago.map((item, idx) => {
                    const maxMonto = getMaxValue(metodosPago, 'monto');
                    const width = (parseFloat(item.monto) / maxMonto) * 100;
                    return (
                      <div key={idx} className="bar-item">
                        <span className="bar-label">{item.tipo_pago}</span>
                        <div className="bar-container">
                          <div className="bar-fill blue" style={{ width: `${width}%` }} />
                        </div>
                        <span className="bar-value">{item.cantidad} - {formatMoney(item.monto)}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Carga de Recepción */}
          {activeTab === 'carga_recepcion' && (
            <div>
              {cargaRecepcion.length > 0 && (
                <div className="card">
                  <h3 className="card-title" style={{ marginBottom: '20px' }}>Órdenes Registradas por Persona</h3>
                  <div className="bar-chart">
                    {cargaRecepcion.map((item, idx) => {
                      const maxCantidad = getMaxValue(cargaRecepcion, 'cantidad');
                      const width = (parseInt(item.cantidad) / maxCantidad) * 100;
                      return (
                        <div key={idx} className="bar-item">
                          <span className="bar-label">{item.recepcionista}</span>
                          <div className="bar-container">
                            <div className="bar-fill blue" style={{ width: `${width}%` }} />
                          </div>
                          <span className="bar-value">{item.cantidad}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              <div className="card">
                <h3 className="card-title" style={{ marginBottom: '20px' }}>Detalle de Recepción</h3>
                {cargaRecepcion.length === 0 ? (
                  <p style={{ color: 'var(--text-light)' }}>No hay datos para mostrar</p>
                ) : (
                  <div className="table-container">
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Registrado por</th>
                          <th>Órdenes</th>
                          <th>Ingresos</th>
                        </tr>
                      </thead>
                      <tbody>
                        {cargaRecepcion.map((row, idx) => (
                          <tr key={idx}>
                            <td style={{ fontWeight: 500 }}>{row.recepcionista}</td>
                            <td>{row.cantidad}</td>
                            <td>{formatMoney(row.ingresos)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Reportes;
